import { useState } from "react";
import { Box, Group, Paper, Stack, Text } from "@mantine/core";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import corridorCardClasses from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";
import { CorridorCardThumbnail } from "@/components/ecosystem-head/EcosystemHeadCorridorCard";
import {
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import { comingSoonCorridorHasFlags } from "@/shared/constants/comingSoonEcosystemCorridors.const";

const CARD_NAVY = "#04305c";
const CARD_BLUE = "#2563EB";

type EcosystemCorridorPlaceholderCardProps = {
  corridor: ComingSoonEcosystemCorridor;
  /** Fixed width for horizontal scroll layouts (e.g. explore page). */
  fixedWidth?: number;
  fullWidth?: boolean;
  /** Use compact flag badges (sector carousel) vs inline flags (explore grid). */
  variant?: "carousel" | "explore";
};

function PlaceholderHeaderFlags({ corridor }: { corridor: ComingSoonEcosystemCorridor }) {
  const originCountry = corridor.country?.trim() || null;
  const targetCountry = corridor.targetCountry?.trim() || null;

  const showFlags =
    comingSoonCorridorHasFlags(corridor) &&
    Boolean(originCountry && countryHasRenderableFlag(originCountry)) &&
    Boolean(targetCountry && countryHasRenderableFlag(targetCountry));

  if (!showFlags || !originCountry || !targetCountry) return null;

  return (
    <Box className={corridorCardClasses.flagPair}>
      <CountryFlag country={originCountry} size="lg" />
      <CorridorDoubleArrow size={12} color={CARD_BLUE} />
      <CountryFlag country={targetCountry} size="lg" />
    </Box>
  );
}

export default function EcosystemCorridorPlaceholderCard({
  corridor,
  fixedWidth,
  fullWidth = false,
  variant = "carousel",
}: EcosystemCorridorPlaceholderCardProps) {
  const isUnderway = corridor.status === "underway";
  const statusLabel = isUnderway ? "Underway" : "Coming Soon";
  const isExplore = variant === "explore";
  const cardHeight = ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT;
  const cardWidth =
    fullWidth && isExplore ? undefined : isExplore ? ECOSYSTEM_EXPLORE_CARD_WIDTH : fixedWidth;
  const [isHovered, setIsHovered] = useState(false);
  const corridorTitle = corridor.corridorSubtitle.trim();
  const countryText = `${corridor.originLabel.trim()} ↔ ${corridor.targetLabel.trim()}`;

  return (
    <Paper
      radius="lg"
      shadow="none"
      h={fullWidth ? "100%" : cardHeight}
      className={corridorCardClasses.card}
      onMouseEnter={isExplore ? () => setIsHovered(true) : undefined}
      onMouseLeave={isExplore ? () => setIsHovered(false) : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${isExplore && isHovered ? "#93C5FD" : "#E2E8F0"}`,
        overflow: "hidden",
        boxShadow: isExplore
          ? isHovered
            ? "0 12px 28px rgba(4, 48, 92, 0.14)"
            : "0 4px 14px rgba(15, 23, 42, 0.06)"
          : "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        minHeight: fullWidth ? 0 : cardHeight,
        ...(cardWidth
          ? { flex: "0 0 auto", width: cardWidth, maxWidth: "85vw" }
          : fullWidth && isExplore
            ? { width: "100%", flex: 1 }
            : undefined),
      }}
    >
      <Box className={corridorCardClasses.cardContent}>
        <Group wrap="nowrap" align="center" gap={10} px={12} py={10} style={{ flex: 1, minHeight: 0 }}>
          <CorridorCardThumbnail title={corridorTitle} />

          <Stack gap={6} justify="space-between" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <PlaceholderHeaderFlags corridor={corridor} />
              <Text
                fw={800}
                fz={14}
                c={CARD_NAVY}
                lineClamp={2}
                style={{
                  lineHeight: 1.25,
                  letterSpacing: "0.01em",
                  overflowWrap: "normal",
                  wordBreak: "normal",
                  hyphens: "none",
                }}
              >
                {corridorTitle}
              </Text>
              <Text fz={12} c="#64748B" fw={600} lineClamp={1} style={{ lineHeight: 1.25 }}>
                {countryText}
              </Text>
            </Stack>
            <Box className={corridorCardClasses.ctaRow}>
              <Box className={corridorCardClasses.ctaComingSoon}>{statusLabel}</Box>
            </Box>
          </Stack>
        </Group>
      </Box>
    </Paper>
  );
}
